import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { setCookie, getConsentStatus } from '../../utils/cookieUtils';

const CookieConsent = () => {
  const [showConsent, setShowConsent] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  
  // Cookie consent options
  const [cookieSettings, setCookieSettings] = useState({
    necessary: true, // Always required
    analytics: true,
    marketing: false,
    preferences: true
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consentStatus = getConsentStatus();
    if (!consentStatus) {
      // Show the banner if no choice has been made
      setShowConsent(true);
    }
  }, []);

  // Safely set localStorage items without interfering with auth
  const safeSetLocalStorage = (key, value) => {
    try {
      // Preserve existing auth data
      const token = localStorage.getItem('token');
      const user = localStorage.getItem('user');
      const pendingVerification = localStorage.getItem('pendingVerification');
      
      // Set the new value
      localStorage.setItem(key, value);
      
      // Make sure auth data is still intact
      if (token) localStorage.setItem('token', token);
      if (user) localStorage.setItem('user', user);
      if (pendingVerification) localStorage.setItem('pendingVerification', pendingVerification);
    } catch (error) {
      console.error(`Error setting ${key} in localStorage:`, error);
    }
  };

  const acceptAllCookies = () => {
    const settings = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true
    };
    
    safeSetLocalStorage('cookieConsent', 'all');
    safeSetLocalStorage('cookieSettings', JSON.stringify(settings));
    
    // Set a cookie for server-side tracking of consent
    setCookie('cookieConsent', 'all', 365); // Valid for 1 year
    
    setShowConsent(false);
  };

  const acceptNecessaryCookies = () => {
    const settings = {
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false
    };
    
    safeSetLocalStorage('cookieConsent', 'necessary');
    safeSetLocalStorage('cookieSettings', JSON.stringify(settings));
    
    // Set a cookie for server-side tracking of consent
    setCookie('cookieConsent', 'necessary', 365); // Valid for 1 year
    
    setShowConsent(false);
  };

  const saveCustomSettings = () => {
    safeSetLocalStorage('cookieConsent', 'custom');
    safeSetLocalStorage('cookieSettings', JSON.stringify(cookieSettings));
    
    // Set a cookie for server-side tracking of consent
    setCookie('cookieConsent', 'custom', 365); // Valid for 1 year
    
    setShowConsent(false);
    setShowCustomize(false);
  };

  const handleSettingChange = (setting) => {
    setCookieSettings({
      ...cookieSettings,
      [setting]: !cookieSettings[setting]
    });
  };

  if (!showConsent) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-md shadow-xl">
      <div 
        className="rounded-xl p-6 text-white"
        style={{
          background: 'linear-gradient(180deg, #301555 0%, #411C73 50%, #1E0B38 100%)',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        {!showCustomize ? (
          <>
            <div className="flex items-center mb-4">
              {/* <div className="mr-3">
                <div className="w-10 h-10 bg-purple-500/30 rounded-full flex items-center justify-center">
                  <svg 
                    className="h-6 w-6 text-purple-300" 
                    viewBox="0 0 24 24" 
                    fill="none"
                    stroke="currentColor"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M21.598 11.064a1.006 1.006 0 00-.854-.172A2.938 2.938 0 0120 11c-1.654 0-3-1.346-3-3 0-.217.031-.444.099-.675a1.006 1.006 0 00-1.026-1.281 2.994 2.994 0 00-2.568 1.43c-.039-.047-.077-.095-.123-.138a2.997 2.997 0 00-2.45-3.282 1 1 0 00-1.146.807C9.536 5.955 9 6.827 9 8c0 .18.018.348.05.521A2.997 2.997 0 006.294 7.06a1.005 1.005 0 00-1.152.673A2.936 2.936 0 015 8c0 1.654 1.346 3 3 3 .217 0 .444-.031.675-.099a1.006 1.006 0 001.281 1.026 2.994 2.994 0 00-1.056 2.526c.018.047.033.095.053.142a2.999 2.999 0 003.266 2.406 1 1 0 00.865-1.021 3.03 3.03 0 01.087-.63 2.999 2.999 0 012.304-2.29 1.006 1.006 0 00.803-1.177 2.987 2.987 0 01-.069-.657c0-1.654 1.346-3 3-3 .384 0 .769.073 1.138.216a1.005 1.005 0 001.251-1.378z" 
                    />
                  </svg>
                </div>
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Cookie Consent</h2>
              </div> */}
            </div>
            <p className="mb-4 text-sm text-gray-300">
              By clicking "Accept all cookies", you agree Zahoot-AI can store cookies on your 
              device and disclose information in accordance with our{' '}
              <Link to="/privacy-policy" className="text-transparent bg-clip-text bg-gradient-to-r from-[#D80364] to-[#F0654B] hover:underline">
                Privacy Policy
              </Link>
              {' '}and{' '}
              <Link to="/cookie-policy" className="text-transparent bg-clip-text bg-gradient-to-r from-[#D80364] to-[#F0654B] hover:underline">
                Cookie Policy
              </Link>.
            </p>
            <div className="flex flex-col space-y-3">
              <button
                onClick={acceptAllCookies}
                className="w-full py-2 px-4 rounded-md font-medium transition-all duration-200"
                style={{background: 'linear-gradient(92.68deg, #D80364 0%, #F0654B 100%)'}}
              >
                Accept all cookies
              </button>
              <button
                onClick={acceptNecessaryCookies}
                className="w-full py-2 px-4 bg-purple-900/50 hover:bg-purple-900/70 text-white rounded-md font-medium transition-all duration-200"
              >
                Necessary cookies only
              </button>
              <button
                onClick={() => setShowCustomize(true)}
                className="w-full py-2 px-4 border border-purple-400/50 hover:border-purple-400 text-purple-300 hover:text-purple-200 rounded-md font-medium transition-all duration-200"
              >
                Customise settings
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 className="text-xl font-bold mb-4 text-white">Customise Cookie Settings</h2>
            <div className="mb-6">
              <div className="mb-4 p-3 bg-purple-900/30 rounded-lg">
                <label className="flex items-center cursor-not-allowed">
                  <input
                    type="checkbox"
                    checked={cookieSettings.necessary}
                    disabled
                    className="mr-2 accent-pink-500"
                  />
                  <span className="font-semibold text-white">Necessary Cookies</span>
                </label>
                <p className="text-gray-300 text-sm ml-6">These cookies are required for the website to function properly.</p>
              </div>
              
              <div className="mb-4 p-3 bg-purple-900/30 rounded-lg">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={cookieSettings.analytics}
                    onChange={() => handleSettingChange('analytics')}
                    className="mr-2 accent-pink-500"
                  />
                  <span className="font-semibold text-white">Analytics Cookies</span>
                </label>
                <p className="text-gray-300 text-sm ml-6">Help us improve our website by collecting anonymous information.</p>
              </div>
              
              <div className="mb-4 p-3 bg-purple-900/30 rounded-lg">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={cookieSettings.marketing}
                    onChange={() => handleSettingChange('marketing')}
                    className="mr-2 accent-pink-500"
                  />
                  <span className="font-semibold text-white">Marketing Cookies</span>
                </label>
                <p className="text-gray-300 text-sm ml-6">Used to track your activity for personalised marketing.</p>
              </div>
              
              <div className="mb-4 p-3 bg-purple-900/30 rounded-lg">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={cookieSettings.preferences}
                    onChange={() => handleSettingChange('preferences')}
                    className="mr-2 accent-pink-500"
                  />
                  <span className="font-semibold text-white">Preference Cookies</span>
                </label>
                <p className="text-gray-300 text-sm ml-6">Remember your preferences and settings.</p>
              </div>
            </div>
            
            <div className="flex flex-col space-y-3">
              <button
                onClick={saveCustomSettings}
                className="w-full py-2 px-4 rounded-md font-medium transition-all duration-200"
                style={{background: 'linear-gradient(92.68deg, #D80364 0%, #F0654B 100%)'}}
              >
                Save preferences
              </button>
              <button
                onClick={() => setShowCustomize(false)}
                className="w-full py-2 px-4 border border-purple-400/50 hover:border-purple-400 text-purple-300 hover:text-purple-200 rounded-md font-medium transition-all duration-200"
              >
                Back
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CookieConsent; 